<template>
  <v-dialog persistent v-model="dialog" width="1000">
    <v-card>
      <v-card-title>
        <span class="title">Usuário</span>
        <v-spacer />
        <v-btn fab text x-small @click="close()">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-form v-model="valid">
          <v-row>
            <v-col class="py-0" cols="12" md="4">
              <v-text-field
                v-model="user.cpf"
                label="CPF"
                v-mask="masks.cpf"
                :rules="[validations.required, validations.cpf]"
                dense
                outlined
              />
            </v-col>
            <v-col class="py-0">
              <v-text-field
                v-model="user.name"
                label="Nome"
                :rules="[validations.required]"
                dense
                outlined
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col class="py-0">
              <v-text-field
                v-model="user.email"
                label="E-mail"
                :rules="[validations.email]"
                dense
                outlined
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col class="py-0" cols="12" md="6">
              <v-text-field
                v-model="user.phone"
                label="Telefone"
                dense
                :rules="user.phone ? [validations.phone] : []"
                outlined
                v-mask="user.phone && user.phone.length == 14 ? masks.phone : masks.cellphone"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col class="py-0" cols="12" md="6">
              <v-switch v-model="user.active" label="Usuário Ativo?" dense outlined />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <div class="d-flex align-center">
                <span
                  :class="{ 'font-weight-bold': true, 'text-uppercase': true, 'error--text': permissionError }"
                >Permissões</span>
                <v-btn
                  icon
                  @click="isAddingPermission = true"
                  color="primary"
                  :disabled="isAddingPermission"
                >
                  <v-icon text>mdi-plus</v-icon>
                </v-btn>
              </div>
            </v-col>
          </v-row>
          <v-row v-show="permissionError">
            <v-col>
              <span>
                <v-icon left class="error--text">mdi-alert-circle-outline</v-icon>
                <span class="error--text">{{ permissionError }}</span>
              </span>
            </v-col>
          </v-row>
          <user-permissions-data-table
            v-model="userPermissions"
            v-show="userPermissions && userPermissions.length"
          ></user-permissions-data-table>
          <user-permissions-form
            v-show="isAddingPermission"
            @newPermission="addPermission"
            @cancel="isAddingPermission = false"
            :alreadySelectedRoles="alreadySelectedRoles"
          ></user-permissions-form>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          @click="save()"
          :disabled="!isUserFormValid"
          :loading="loading"
        >Salvar</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from 'vuex';
import { get, find, map, groupBy, keys, set, head } from 'lodash';
import { validations, masks, apiResponseMixin } from '@/mixins';
import usersService from '@/services/users-service';
import constants from '@/constants';
import { clone, transformProps, replaceKeys } from '@/utils';
import UserPermissionsDataTable from '@/components/user/UserPermissionsDataTable';
import UserPermissionsForm from '@/components/user/UserPermissionsForm';

export default {
  name: 'create-or-update-user',
  mixins: [validations, masks, apiResponseMixin],
  props: ['dialog', 'selectedUser'],
  components: { UserPermissionsDataTable, UserPermissionsForm },
  data: () => ({
    valid: false,
    user: {},
    loading: false,
    isAddingPermission: false,
    userPermissions: []
  }),
  methods: {
    ...mapActions('base', ['showSnackbar']),
    save() {
      this.loading = true;
      const payload = this.parseUserRequest({ ...this.user, userPermissions: this.userPermissions });
      usersService
        .save(payload)
        .then(() => {
          this.showSnackbar({ show: true, message: 'Usuário cadastrada com sucesso', color: 'success' });
          this.close({ forceRefresh: true });
        })
        .catch(err => {
          console.log(err);
          this.handleApiErrorResponse(err);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    close(payload = { forceRefresh: false }) {
      this.$emit('closeDialog', payload);
    },
    addPermission(newPermission) {
      this.userPermissions.push(newPermission);
      this.isAddingPermission = false;
    },
    parseUserRequest(userData) {
      const parsedData = clone(userData);
      transformProps(parsedData, 'cpf', this.unmask);
      transformProps(parsedData, 'userPermissions', permissions =>
        permissions.reduce((acc, curr) => {
          const pushToAcc = unit => acc.push({ role_type: curr.role, unit_id: get(unit, 'id') });
          curr.units.length ? curr.units.forEach(pushToAcc) : pushToAcc({});
          return acc;
        }, [])
      );
      replaceKeys(parsedData, { userPermissions: 'roles_attributes' });
      return parsedData;
    },
    parseUserPermissions(userPermissions) {
      const groupedPermissions = groupBy(userPermissions, role => role.role_type);
      const permissionsNames = keys(groupedPermissions).reduce((acc, curr) => set(acc, curr, get(head(groupedPermissions[curr]), 'name')), {})
      const parsedPermissions = keys(groupedPermissions).reduce((acc, curr) => {
        acc.push({ role: curr, units: map(groupedPermissions[curr], 'unit'), name: permissionsNames[curr] });
        return acc;
      }, []);
      this.userPermissions = parsedPermissions;
    },
  },
  computed: {
    permissionError() {
      return this.isAddingPermission || (this.userPermissions && this.userPermissions.length > 0) ? false : 'Selecione ao menos uma permissão';
    },
    isUserFormValid() {
      return this.valid && !this.permissionError;
    },
    alreadySelectedRoles() {
      return map(this.userPermissions, 'role');
    }
  },
  watch: {
    dialog(newValue) {
      if (newValue) {
        this.user = Object.assign({}, this.selectedUser);
        this.parseUserPermissions(get(this.selectedUser, 'roles'));
      } else {
        this.user = {};
        this.newPermission = {};
        this.userPermissions = [];
        this.isAddingPermission = false;
      }
    }
  }
};
</script>
